import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { UserPlus, Trash2, Save, Loader2, Users, ShieldCheck } from "lucide-react";
import AdminLayout from "../components/AdminLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_BADGES = {
  super_admin: "bg-[color:var(--gold)] text-black",
  catalog_manager: "bg-blue-500/15 text-blue-300 border border-blue-400/30",
  order_preparer: "bg-emerald-500/15 text-emerald-300 border border-emerald-400/30",
  logistics: "bg-orange-500/15 text-orange-300 border border-orange-400/30",
  accountant: "bg-purple-500/15 text-purple-300 border border-purple-400/30",
};

const ROLE_DESC = {
  super_admin: "Accès complet à toutes les sections du back-office",
  catalog_manager: "Produits, photos/vidéos, contenu, collections, avis",
  order_preparer: "Consulter et préparer les commandes",
  logistics: "Suivi expéditions, transporteurs, numéros de tracking",
  accountant: "Paiements, factures, statistiques financières",
};

export default function AdminTeam() {
  const [me, setMe] = useState(null);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ email: "", full_name: "", password: "", role: "catalog_manager" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [w, t] = await Promise.all([
        axios.get(`${API}/admin/whoami`, { withCredentials: true }),
        axios.get(`${API}/admin/team`, { withCredentials: true }),
      ]);
      setMe(w.data);
      setTeam(t.data);
    } catch (e) {
      if (e.response?.status === 403) toast.error("Réservé au super administrateur");
      else toast.error("Chargement impossible");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/team`, form, { withCredentials: true });
      setTeam((prev) => [...prev, data]);
      setForm({ email: "", full_name: "", password: "", role: "catalog_manager" });
      setShowAdd(false);
      toast.success("Membre ajouté");
    } catch (e) { toast.error(e.response?.data?.detail || "Création impossible"); }
    finally { setBusy(false); }
  };

  const changeRole = async (id, role) => {
    try {
      await axios.patch(`${API}/admin/team/${id}`, { role }, { withCredentials: true });
      setTeam((prev) => prev.map((u) => u.id === id ? { ...u, role } : u));
      toast.success("Rôle mis à jour");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const resetPassword = async (id) => {
    const p = window.prompt("Nouveau mot de passe (8 caractères min.)");
    if (!p || p.length < 8) return;
    try {
      await axios.patch(`${API}/admin/team/${id}`, { password: p }, { withCredentials: true });
      toast.success("Mot de passe réinitialisé");
    } catch { toast.error("Impossible"); }
  };

  const del = async (id, email) => {
    if (!window.confirm(`Retirer ${email} de l'équipe ?`)) return;
    try {
      await axios.delete(`${API}/admin/team/${id}`, { withCredentials: true });
      setTeam((prev) => prev.filter((u) => u.id !== id));
      toast.success("Membre retiré");
    } catch (e) { toast.error(e.response?.data?.detail || "Impossible"); }
  };

  if (loading) {
    return <AdminLayout current="team"><div className="text-white/40">Chargement…</div></AdminLayout>;
  }

  if (me && me.role !== "super_admin") {
    return (
      <AdminLayout current="team">
        <div className="max-w-md text-center py-20">
          <ShieldCheck size={40} className="text-white/20 mx-auto mb-4" />
          <h1 className="font-serif text-3xl mb-3">Accès refusé</h1>
          <p className="text-white/50 text-sm">La gestion d'équipe est réservée au super administrateur.</p>
        </div>
      </AdminLayout>
    );
  }

  const roles = me?.roles_labels || {};

  return (
    <AdminLayout current="team">
      <div className="max-w-5xl" data-testid="admin-team-page">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <p className="label-caps text-[color:var(--gold)] text-[10px] tracking-[0.3em] mb-2">Organisation</p>
            <h1 className="font-serif text-4xl flex items-center gap-3"><Users size={28} className="text-[color:var(--gold)]" strokeWidth={1.5} /> Équipe</h1>
            <p className="text-white/50 text-sm mt-2">Invitez comptable, préparateur, logistique et gestionnaire de catalogue. Chacun n'accède qu'aux sections nécessaires à son travail.</p>
          </div>
          <button data-testid="add-member-btn" onClick={() => setShowAdd((v) => !v)}
            className="inline-flex items-center gap-2 bg-[color:var(--gold)] text-black hover:bg-white px-5 py-3 label-caps text-[11px] tracking-[0.25em]">
            <UserPlus size={14} /> Ajouter un membre
          </button>
        </div>

        {showAdd && (
          <form onSubmit={create} className="border border-[color:var(--gold)]/30 p-6 mb-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="new-member-form">
            <input required placeholder="Nom complet" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="input-line" data-testid="tm-name" />
            <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-line" data-testid="tm-email" />
            <input required type="password" minLength={8} placeholder="Mot de passe temporaire (8+ car.)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="input-line" data-testid="tm-password" />
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="input-line" data-testid="tm-role">
              {Object.entries(roles).filter(([k]) => k !== "super_admin").map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <p className="md:col-span-2 text-white/40 text-xs">{ROLE_DESC[form.role]}</p>
            <button type="submit" disabled={busy} data-testid="submit-member" className="md:col-span-2 inline-flex items-center justify-center gap-2 bg-[color:var(--gold)] text-black hover:bg-white px-5 py-3 label-caps text-[11px] tracking-[0.25em] disabled:opacity-40">
              {busy ? <Loader2 className="animate-spin" size={14} /> : <UserPlus size={14} />} Créer le compte
            </button>
          </form>
        )}

        <div className="space-y-3" data-testid="team-list">
          {team.map((u) => (
            <div key={u.id} className="border border-white/10 p-5 flex items-center justify-between gap-4 flex-wrap hover:border-white/25 transition-colors" data-testid={`member-${u.id.slice(0, 8)}`}>
              <div className="min-w-0 flex-1">
                <p className="font-serif text-xl truncate">{u.full_name || "—"}</p>
                <p className="text-white/50 text-sm">{u.email}</p>
                <p className="text-white/30 text-[10px] mt-1">{u.created_at && new Date(u.created_at).toLocaleDateString("fr-FR")}</p>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                {u.email === me?.email ? (
                  <span className={`label-caps text-[10px] px-3 py-1.5 tracking-[0.2em] ${ROLE_BADGES[u.role]}`}>{roles[u.role] || u.role} (vous)</span>
                ) : (
                  <select value={u.role} onChange={(e) => changeRole(u.id, e.target.value)} className="input-line py-1.5 text-xs" data-testid={`role-${u.id.slice(0, 8)}`}>
                    {Object.entries(roles).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                )}
                {u.email !== me?.email && (
                  <>
                    <button onClick={() => resetPassword(u.id)} className="border border-white/15 hover:border-[color:var(--gold)] text-white/60 hover:text-[color:var(--gold)] px-3 py-1.5 label-caps text-[10px]" data-testid={`reset-${u.id.slice(0, 8)}`}>Réinitialiser mdp</button>
                    <button onClick={() => del(u.id, u.email)} className="border border-white/15 hover:border-red-400 text-white/40 hover:text-red-400 px-3 py-1.5" data-testid={`del-${u.id.slice(0, 8)}`}><Trash2 size={12} /></button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Role reference card */}
        <div className="mt-12 border border-white/5 p-6 bg-white/[0.02]">
          <p className="label-caps text-[10px] text-white/50 tracking-[0.3em] mb-4">Rôles disponibles</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(roles).map(([k, l]) => (
              <div key={k} className="flex items-start gap-3">
                <span className={`label-caps text-[10px] px-2.5 py-1 tracking-[0.2em] whitespace-nowrap ${ROLE_BADGES[k]}`}>{l}</span>
                <p className="text-white/50 text-xs leading-relaxed">{ROLE_DESC[k]}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
